// App Migration Banner Component
// Displays a floating pill-shaped banner on mobile web to encourage app usage
// Features auto-collapse after 5 seconds: center position → right side with logo only

import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Helpers
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// Hooks
import { useAppInstallDetection } from 'shared/components/AppMigration/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// Helpers
import {
  isBannerDismissed,
  dismissBanner,
  navigateToURL,
  getAppMigrationOneLinkUrl,
  isIOSDevice,
} from 'shared/components/AppMigration/helpers';
import {
  buildAppMigrationInteractionLocation,
  getAppMigrationAnalyticsPage,
} from 'shared/components/AppMigration/analytics';

// Constants
import {
  CTA_TEXT,
  BANNER_TEXT,
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_A,
  STORE_URLS,
} from 'shared/components/AppMigration/constants';

// Styles
import {
  BannerContainer,
  LogoContainer,
  TextContent,
  BannerText,
  CloseButton,
} from 'shared/components/AppMigration/AppMigrationBanner/styles';

// Components
import AppMigrationBottomSheet, {
  preloadBottomSheetImages,
} from 'shared/components/AppMigration/AppMigrationBottomSheet';

// Icons
import { FashionLogoSmall, CloseCircleSmallIcon } from 'shared/components/AppMigration/icons';

// Types
import { IAppMigrationBannerProps, BannerCTAText } from 'shared/components/AppMigration/types';

// Auto-collapse delay in milliseconds (5 seconds)
const AUTO_COLLAPSE_DELAY = 5000;

/**
 * AppMigrationBanner - A floating pill-shaped banner that encourages users to use the app
 *
 * Features:
 * - Detects if app is installed on Android (uses getInstalledRelatedApps API)
 * - Integrates with AppsFlyer Smart Script for OneLink URL generation
 * - Shows "Use App" or "Download App" based on installation status
 * - Can be dismissed (persists for 24 hours)
 * - Auto-collapses after 5 seconds: moves from center to right with logo only
 * - Clicking collapsed banner expands it back
 *
 * @param pageType - The page type for analytics tracking
 * @param className - Optional CSS class for styling overrides
 * @param onClose - Callback when banner is closed
 * @param onCTAClick - Callback when CTA is clicked
 */
function AppMigrationBanner({
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onClose,
  onCTAClick,
}: IAppMigrationBannerProps) {
  const { search } = useLocation();
  const isSearchOpen = search.includes('showsearch');

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  // Ref for auto-collapse timer
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref for close animation timer
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const appMigrationEnabled = useRemoteConfig('appMigrationEnabled');
  const appsFlyerLinks = useRemoteConfig('appMigrationAppsFlyerLinks');

  // AB Experiment for feature flag
  const { isEligible = false, variant = '' } = useEligibleSSRVariantName(APP_MIGRATION_EXP);
  const isFeatureEnabled =
    !!appMigrationEnabled && isEligible && variant === APP_MIGRATION_EXP_VARIANT_A;

  // App installation detection
  const { isLoading: isDetecting, isMobile, platform, isAppInstalled } = useAppInstallDetection();

  // Initialize banner visibility
  useEffect(() => {
    // Wait for detection to complete
    if (isDetecting) return;

    // Check if feature is enabled via AB experiment
    if (!isFeatureEnabled) {
      setIsLoading(false);
      return;
    }

    // Only show on mobile web
    if (!isMobile) {
      setIsLoading(false);
      return;
    }

    // Check if banner was dismissed recently
    if (isBannerDismissed()) {
      setIsLoading(false);
      return;
    }

    // Preload bottom sheet images for faster loading when opened
    preloadBottomSheetImages();

    // Show the banner
    setIsVisible(true);
    setIsLoading(false);
  }, [isDetecting, isMobile, isFeatureEnabled]);

  // Auto-collapse timer: collapse after 5 seconds when visible
  useEffect(() => {
    if (!isVisible || isCollapsed) return;

    // Start the collapse timer
    collapseTimerRef.current = setTimeout(() => {
      setIsCollapsed(true);
    }, AUTO_COLLAPSE_DELAY);

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [isVisible, isCollapsed]);

  // Cleanup close animation timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Determine CTA text based on platform and app install status
   * - iOS: Always "Download Now" (cannot detect app install reliably)
   * - Android with app installed: "Use App"
   * - Android without app: "Download Now"
   */
  const getCTAText = useCallback((): BannerCTAText => {
    if (platform === 'ios') {
      return CTA_TEXT.DOWNLOAD_NOW;
    }
    if (platform === 'android' && isAppInstalled) {
      return CTA_TEXT.USE_APP;
    }
    return CTA_TEXT.DOWNLOAD_NOW;
  }, [platform, isAppInstalled]);

  /**
   * Handle banner click - opens bottom sheet
   * Works for both expanded and collapsed states
   */
  const handleBannerClick = useCallback(() => {
    // Open the bottom sheet on any click (expanded or collapsed)
    setIsBottomSheetOpen(true);
  }, []);

  /**
   * Handle bottom sheet close
   */
  const handleBottomSheetClose = useCallback(() => {
    setIsBottomSheetOpen(false);
  }, []);

  /**
   * Handle download button click in bottom sheet — uses remote-config OneLink
   */
  const handleDownload = useCallback(() => {
    const oneLinkUrl = getAppMigrationOneLinkUrl(appsFlyerLinks, 'bottomSheet');
    const url = oneLinkUrl || (isIOSDevice() ? STORE_URLS.APP_STORE : STORE_URLS.PLAY_STORE);

    if (!oneLinkUrl) {
      log({
        errTitle:
          '[AppMigration] Missing bottomSheet OneLink in remote config, using store fallback',
        tags: { [TAG_IDENTIFIER.MODULE]: 'AppMigrationBanner' },
      });
    }

    if (onCTAClick) {
      onCTAClick(url);
    }

    navigateToURL(url);
  }, [appsFlyerLinks, onCTAClick]);

  /**
   * Handle close button click
   * Hides the banner with animation and persists dismissal for 24 hours
   */
  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent triggering banner click
      setIsClosing(true);

      // Wait for animation to complete before hiding
      closeTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);

        // Persist dismissal
        dismissBanner();

        if (onClose) {
          onClose();
        }
      }, 300);
    },
    [onClose]
  );

  const analyticsInteractionLocation = buildAppMigrationInteractionLocation(
    getAppMigrationAnalyticsPage(),
    'floater'
  );

  // Don't render during SSR
  if (typeof window === 'undefined') {
    return null;
  }

  // Don't render if feature flag is disabled
  if (!isFeatureEnabled) {
    return null;
  }

  // Don't render when search is open
  if (isSearchOpen) {
    return null;
  }

  // Don't render while loading
  if (isLoading) {
    return null;
  }

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  const ctaText = getCTAText();

  return (
    <>
      <BannerContainer
        className={className}
        onClick={handleBannerClick}
        data-testid="app-migration-banner"
        aria-label={
          isCollapsed
            ? 'Expand app banner'
            : `${ctaText} - ${BANNER_TEXT.LINE_1} ${BANNER_TEXT.LINE_2}`
        }
        isCollapsed={isCollapsed}
        isClosing={isClosing}
      >
        {/* Nykaa Fashion Logo */}
        <LogoContainer>
          <FashionLogoSmall />
        </LogoContainer>

        {/* Banner Text - hidden when collapsed */}
        <TextContent isCollapsed={isCollapsed}>
          <BannerText>{BANNER_TEXT.LINE_1}</BannerText>
          <BannerText>{BANNER_TEXT.LINE_2}</BannerText>
        </TextContent>

        {/* Close Button - positioned absolutely, hidden when collapsed */}
        <CloseButton
          onClick={handleClose}
          aria-label="Close banner"
          role="button"
          tabIndex={0}
          isCollapsed={isCollapsed}
        >
          <CloseCircleSmallIcon />
        </CloseButton>
      </BannerContainer>

      {/* Bottom Sheet */}
      <AppMigrationBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleBottomSheetClose}
        onDownload={handleDownload}
        analyticsInteractionLocation={analyticsInteractionLocation}
        ctaText={getCTAText()}
      />
    </>
  );
}

export default memo(AppMigrationBanner);
